import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useState } from "react"
import { Link } from "react-router-dom"
import { Karte, Knopf, Leer } from "@/components/ui"
import { api, fehlertext } from "@/lib/api"
import { kurz, kw, plusTage, wochentag } from "@/lib/datum"
import { cn } from "@/lib/format"

interface Fehlend { name: string; stelle: string; status: "fehlt" | "nicht erschienen" }
interface Tag { datum: string; arbeitstag: boolean; kalender: string; soll: number; da: number; fehlt: number; fehlende: Fehlend[] }
interface Schicht { nr: number; name: string; modell: string; beginn: string; ende: string; team: string; tage: Tag[] }
interface Woche { eingerichtet: boolean; montag?: string; kw?: number; heute?: string; tage?: string[]; schichten: Schicht[] }

/** Schichtwoche nach Plan (Ninox 15-Schichtplanung): je Schicht und Tag anwesend und fehlt, mit Blättern. */
export function SchichtWoche() {
  const [montag, setMontag] = useState<string | null>(null) // null = laufende Woche
  const [auswahl, setAuswahl] = useState<{ nr: number; datum: string } | null>(null)
  const abfrage = useQuery({
    queryKey: ["schichtwoche", montag],
    queryFn: async () => (await api.get<Woche>("/schichtplan/woche", { params: montag ? { montag } : {} })).data,
    placeholderData: keepPreviousData,
    refetchInterval: 300_000,
  })
  const d = abfrage.data
  const blaettern = (tage: number | null) => {
    setAuswahl(null)
    setMontag(tage === null || !d?.montag ? null : plusTage(d.montag, tage))
  }
  const gewaehlt = auswahl && d?.schichten.find((s) => s.nr === auswahl.nr)
  const gewaehlterTag = gewaehlt?.tage.find((t) => t.datum === auswahl?.datum)

  const titel = d?.montag && d.tage ? `Schichtwoche KW ${d.kw} · ${kurz(d.montag)}–${kurz(d.tage[6])}` : "Schichtwoche"
  const navigation = d?.montag ? (
    <div className="flex flex-wrap items-center gap-1.5">
      <Knopf onClick={() => blaettern(-7)} title="Vorwoche">
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />{`KW ${kw(plusTage(d.montag, -7))}`}
      </Knopf>
      {montag !== null && <Knopf onClick={() => blaettern(null)}>Diese Woche</Knopf>}
      <Knopf onClick={() => blaettern(7)} title="Folgewoche">
        {`KW ${kw(plusTage(d.montag, 7))}`}<ChevronRight className="h-4 w-4" aria-hidden="true" />
      </Knopf>
    </div>
  ) : undefined

  return (
    <Karte
      titel={titel}
      unter="Nach Plan aus der Schichtplanung: Zahl = anwesend, rot = fehlt (Klick zeigt die Namen, ohne Gründe). Freie Tage mit „–“. Klick auf einen Tag zeigt die Stellenbesetzung."
      rechts={navigation}
    >
      {abfrage.isError ? (
        <p className="m-0 text-sm text-crit">{fehlertext(abfrage.error, "Die Schichtplanung ist gerade nicht erreichbar.")}</p>
      ) : !d ? (
        <Leer>Wird geladen …</Leer>
      ) : !d.eingerichtet ? (
        <Leer>Die Schichtplanung ist noch nicht angebunden.</Leer>
      ) : d.schichten.length === 0 ? (
        <Leer>In der Schichtplanung sind noch keine Schichten angelegt.</Leer>
      ) : (
        <>
          <div className={cn("overflow-x-auto", abfrage.isFetching && "opacity-60")}>
            <table className="w-full min-w-[640px] border-collapse text-[13px]">
              <thead>
                <tr className="text-[11.5px] text-muted">
                  <th className="sticky left-0 z-10 bg-surface px-2 py-1.5 text-left font-medium">Schicht</th>
                  {(d.tage ?? []).map((tag, i) => {
                    const kal = d.schichten.find((s) => s.tage[i]?.kalender && s.tage[i].kalender !== "Wochenende")?.tage[i].kalender
                    return (
                      <th key={tag} className={cn("px-1.5 py-1.5 text-center font-medium", tag === d.heute && "rounded-t-md bg-accent/10 text-accent-strong")}>
                        <Link to={`/stellen?tag=${tag}`} className="block text-inherit no-underline hover:underline" title="Stellenbesetzung an diesem Tag">
                          {`${wochentag(tag)} ${kurz(tag)}`}
                        </Link>
                        {kal && <div className="truncate text-[10.5px] font-normal" title={kal}>{kal}</div>}
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {d.schichten.map((s) => (
                  <tr key={s.nr} className="border-t border-grid">
                    <td className="sticky left-0 z-10 bg-surface px-2 py-2">
                      <div className="font-medium">{s.name}</div>
                      <div className="text-[11.5px] text-muted">{`${s.beginn}–${s.ende}${s.team ? ` · ${s.team}` : ""}`}</div>
                    </td>
                    {s.tage.map((t) => {
                      const aktiv = auswahl?.nr === s.nr && auswahl.datum === t.datum
                      return (
                        <td key={t.datum} className={cn("px-1.5 py-2 text-center tabular", t.datum === d.heute && "bg-accent/5")}>
                          {!t.arbeitstag ? (
                            <span className="text-muted" title={t.kalender || "frei"}>–</span>
                          ) : t.soll === 0 ? (
                            <span className="text-muted" title="niemand eingeplant">0</span>
                          ) : (
                            <button
                              type="button"
                              disabled={t.fehlt === 0}
                              onClick={() => setAuswahl(aktiv ? null : { nr: s.nr, datum: t.datum })}
                              title={`${t.soll} eingeplant · ${t.da} anwesend · ${t.fehlt} fehlen`}
                              className={cn(
                                "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5",
                                t.fehlt > 0 && "cursor-pointer hover:bg-crit/10",
                                aktiv && "bg-crit/15 ring-1 ring-crit/40",
                              )}
                            >
                              <span>{t.da}</span>
                              {t.fehlt > 0 && <span className="rounded bg-crit/15 px-1 text-[11px] font-semibold text-crit">{`−${t.fehlt}`}</span>}
                            </button>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {gewaehlt && gewaehlterTag && (
            <div className="rounded-lg bg-crit/10 px-3 py-2 text-[13px]">
              <div className="mb-1 font-medium">{`${gewaehlt.name} · ${wochentag(gewaehlterTag.datum)} ${kurz(gewaehlterTag.datum)}: es fehlen`}</div>
              <ul className="m-0 grid gap-0.5 pl-4">
                {gewaehlterTag.fehlende.map((p) => (
                  <li key={p.name + p.stelle}>
                    <b>{p.name}</b>
                    <span className={cn("ml-2", p.status === "nicht erschienen" ? "font-medium text-crit" : "text-ink2")}>{p.status}</span>
                    {p.stelle && <span className="ml-2 text-[11.5px] text-muted">{p.stelle}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </Karte>
  )
}
